import { Injectable, PLATFORM_ID, Inject, NgZone } from '@angular/core';
import { HttpClient, HttpResponse, HttpHeaders } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { TransferState, makeStateKey } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
// store
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';

// services
import { BookingService } from '../../booking/services/booking.service';
import { DataStorageService } from './data-storage.service';
import { EventsService } from '../../booking/services/events.service';
import { LoginService } from '../../shared/services/login.service';
import { BagItemsService } from './bagItems.service';
import { CopyService } from './app-copy.service';
import { RulesCopyService } from './app-rules-copy.service';
// models
import { AppState } from '../../booking/models/app-state.interface';
import { initialManualFlightData } from '../../booking/models/booking-init.model';
// reducers

const STORE_INIT_KEY = makeStateKey('store_init');

@Injectable({
  providedIn: 'root',
})
export class AppStateService {

  private _userSavedSubscription: Subscription;
  private _dataIncompleteSave: Subscription;
  private _dataUpgradeSave: Subscription;
  private _bookingProduct: string;
  private _storeInitUsed = false;

  public bookingProductType$: Subject<any> = new Subject();
  public userLoginValid$: Subject<any> = new Subject();
  public bookingProductUpgrade$: Subject<any> = new Subject();
  public completeBookingRefreshed$: BehaviorSubject<any> = new BehaviorSubject(null);
  public completeBookingRefreshedFromStore$: BehaviorSubject<any> = new BehaviorSubject(null);
  public storeInitComplete$: Subject<any> = new Subject();
  public userAPIResponse$: Subject<any> = new Subject();
  public flightFinderRefreshed$: BehaviorSubject<any> = new BehaviorSubject(false);
  public token;
  public bookingRef;

  constructor(
    private _appStore: Store<AppState>,
    private _bookingService: BookingService,
    private _dataStorageService: DataStorageService,
    private _loginService: LoginService,
    private _eventsService: EventsService,
    private _router: Router,
    private _serverState: TransferState,
    private _bagItemsService: BagItemsService,
    private _copyService: CopyService,
    private _rulesCopyService: RulesCopyService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  // get store state
  getState(store: Store<AppState>): AppState {
    let state: AppState;
    store.pipe(take(1)).subscribe(s => state = s);
    return state;
  }

  setAppState(type?, product?) {
    this._bookingProduct = product;
    const state = this.getState(this._appStore);
    // check if cookie set
    if (this._dataStorageService.getCookieDataID() === null || state.bookingReducer.bookingDetails.bookingPaid) {
      this._router.navigate(['flight-finder/search'], { queryParams: { 'start-booking': true } });
      return;
    } else if (type === 'incomplete') { // TODO:
      const departureAirportCode = state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0].departureAirport.airportCode;
      const isHubdropBooking = state.utilityReducer.isHubdropBooking;
      const flightStatusDetails = state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0];
      const airlineCode = flightStatusDetails.airlineCode === 'LX' && flightStatusDetails.operatingAirlineCode === 'LX' ? 'LX' : 'None';
      this._eventsService.generatePaths(this._bookingProduct, departureAirportCode, isHubdropBooking, airlineCode);
      this.setBookingRefreshState(state);
    } else if (type === 'amend') {
      if (state.bookingReducer.bookingDetails.bookingFlightDateAmend) {
        this._bookingProduct = state.bookingReducer.bookingDetails.bookingProduct;
        const departureAirportCode = state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0].departureAirport.airportCode;
        const isHubdropBooking = state.utilityReducer.isHubdropBooking;
        const flightStatusDetails = state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0];
        const airlineCode = flightStatusDetails.airlineCode === 'LX' && flightStatusDetails.operatingAirlineCode === 'LX' ? 'LX' : 'None';
        this._eventsService.generatePaths(this._bookingProduct, departureAirportCode, isHubdropBooking, airlineCode);
      } else {
        // check booking product type
        const isHubdropBooking = state.utilityReducer.isHubdropBooking;
        this._eventsService.generatePathsForAmend(
          this._bookingProduct, this._eventsService.setPathsForAmends(state.bookingReducer.bookingDetails.bookingAmendSection, isHubdropBooking)
        );
      }

      this.setBookingRefreshState(state);
    } else if (state.loadedStatusReducer.flightLoaded) {
      // store already loaded - coming from flight finder
      // refresh from loaded store
      this.getBookingStateFromStore();
    } else if (type === 'new' || type === 'refresh') {
      // get stored data from API
      // not coming from flight finder
      this.getBookingStateFromAPI();
    }
  }

  // set init values in store
  setStoreStateInit(checkUser?) {

    const storeInit = this._serverState.get(STORE_INIT_KEY, null as any);
    if (!storeInit && !this._storeInitUsed) {
      this._dataStorageService.getSavedDataPromise().then((res: AppState) => {
        this._serverState.set(STORE_INIT_KEY, res as any);
        this.setStoreItems(res, isPlatformBrowser(this.platformId) ? true : false);
      });
    } else if (this._storeInitUsed) {
      this._dataStorageService.getSavedDataPromise(true).then((res: AppState) => {
        this.setStoreItems(storeInit, checkUser);
      });
    } else {
      this.setStoreItems(storeInit, checkUser);
      this._storeInitUsed = true;
    }

  }

  setStoreItems(s: any, checkUser: boolean) {
    console.log('store-reset', s);
    this._appStore.dispatch({ type: 'CURRENT_BOOKING_LOADED', payload: false });

    this.resetFlightData();
    this.resetSelectedMultilegFlight();
    this.resetCurrentBookingData();
    this.resetBookingStateData();
    this.resetPriceData();
    this.resetTimeslotData();
    this.resetHubDropReducer();
    // this.resetMilesData();
    // this.resetD2dBookingData();
    // this.resetUserData();
    // this.resetUserAgentData();
    this._appStore.dispatch({ type: 'PANEL_SET', payload: 1 });

    if (checkUser) {
      this.checkUserLogin();
    } else {
      this.resetUserData();
    }

    this.storeInitComplete$.next(true);

  }

  // set new booking values and call service to init booking object
  // setBookingStateInit() {

  //   this._appStore.dispatch({ type: 'UPDATE_BOOKING_PRODUCT', payload: 'abc'});

  //   const journeyTypeValue = this.getProductTypeAsNumber('abc');
  //   const journeyDirectionValue = this.getDirectionAsNumber('outbound');

  //   this.setJourneyDetails(1, journeyTypeValue, journeyDirectionValue);
  //   // this.setVirtualBags(0);
  //   this.setPrice('0');

  //   // create booking init object
  //   // also set bookingInit value to true - used on all booking values to test if data is loaded
  //   this._bookingService.createBookingInit('abc');

  // }
  // called on refresh
  setStoreRefreshState(data, isComplete?, amendFlight?) {
    this._appStore.dispatch({ type: 'REFRESH_BOOKING_STATE', payload: data.bookingReducer });

    // load translations by airline code
    const airlineCode = data?.bookingReducer.bookingDetails?.carrier?.airlineCode || 'OTHER';
    if (airlineCode) {
      this._copyService.getCopy(airlineCode);

      if (airlineCode.toLowerCase() !== "other") {
        this._rulesCopyService.getStrapiCopy(airlineCode);
      }
    }

    this._appStore.dispatch({ type: 'REFRESH_BOOKING_OBJECT', payload: data.currentBookingReducer });
    this._appStore.dispatch({ type: 'REFRESH_FLIGHT_STATE', payload: data.flightReducer });
    if (!amendFlight) {
      this._appStore.dispatch({ type: 'REFRESH_FLIGHT_FINDER', payload: data.flightFinderReducer });
    }
    this._appStore.dispatch({ type: 'REFRESH_TIMESLOT_STATE', payload: data.timeslotReducer });
    this._appStore.dispatch({ type: 'REFRESH_D2D_BOOKING', payload: data.d2dReducer });
    this._appStore.dispatch({ type: 'REFRESH_HUBDROP', payload: data.hubDropReducer });
    this._appStore.dispatch({ type: 'REFRESH_FLYBACK_REDUCER', payload: data.flybackReducer });
    this._appStore.dispatch({ type: 'REFRESH_UPSELL_BOOKING', payload: data.upsellBookingReducer });
    this._appStore.dispatch({ type: 'REFRESH_MILES_REEDUCER', payload: data.milesAndMoreReducer });
    this._appStore.dispatch({ type: 'REFRESH_UTILITY_REDUCER', payload: data.utilityReducer });
    this._appStore.dispatch({ type: 'REFRESH_OLD_BOOKINF_INFO_REDUCER', payload: data.oldBookingInfoReducer });
    this._appStore.dispatch({ type: 'FLIGHT_LOADED', payload: true });
    this._appStore.dispatch({ type: 'STORE_LOADED', payload: true });

    if (data.bookingReducer.bookingDetails.bookingType !== 'amend') {
      this._appStore.dispatch({ type: 'PANEL_SET', payload: data.updatePanelReducer });
    } else {
      this._appStore.dispatch({ type: 'PANEL_SET', payload: 1 });
    }

    this._dataStorageService.saveData();
  }

  setBookingStores(data) {
    this._appStore.dispatch({ type: 'REFRESH_BOOKING_OBJECT', payload: data.currentBookingReducer });
    this._appStore.dispatch({ type: 'REFRESH_BOOKING_STATE', payload: data.bookingReducer });
    this._appStore.dispatch({ type: 'REFRESH_TIMESLOT_STATE', payload: data.timeslotReducer });
    this._appStore.dispatch({ type: 'REFRESH_HUBDROP', payload: data.hubDropReducer });
    // this._appStore.dispatch({ type: 'REFRESH_FLYBACK_REDUCER', payload: data.flybackReducer });
    this._appStore.dispatch({ type: 'REFRESH_OLD_BOOKINF_INFO_REDUCER', payload: data.oldBookingInfoReducer });
    this._appStore.dispatch({ type: 'REFRESH_FLIGHT_STATE', payload: data.flightReducer });
    this._appStore.dispatch({ type: 'FLIGHT_LOADED', payload: true });
  }

  refreshPartner(data) {

    // set all saved partners to NONE
    // remove if using partners again
    data.partnerReducer.partnerName = 'NONE';

    if (typeof data.partnerReducer === 'undefined') {
      this._appStore.dispatch({ type: 'RESET_PARTNER' });
    } else {
      if (data.partnerReducer.partnerName === 'None') { data.partnerReducer.partnerName = 'NONE'; }
      this._appStore.dispatch({ type: 'REFRESH_PARTNER', payload: data.partnerReducer });
    }
  }

  refreshFlightQuoteState(count, page) {

    let callCount = 0;
    const flightFinderStore = this._dataStorageService.getState(this._appStore).flightFinderReducer;
    if (page === 'results' && (flightFinderStore.quoteFlightResults === null || flightFinderStore.quoteFlightResults.length === 0)) {
      this._dataStorageService.getSavedDataPromise(true).then((store: AppState) => {
        // store.flightFinderReducer.requestCount = 0;
        this.refreshFlightFinder(callCount, count, store.flightFinderReducer);
        callCount++;
      });
    } else {
      this.refreshFlightFinder(callCount, count, flightFinderStore);
      callCount++;
      return;
    }
  }

  refreshFlightFinder(callCount, count, store) {
    if (callCount !== count) {
      this._appStore.dispatch({ type: 'REFRESH_FLIGHT_FINDER', payload: store });
      this.flightFinderRefreshed$.next(true);
    }
  }

  amendFlightDestination(data) {

    if (data.currentBookingReducer.currentBooking.makePaymentRequest.sbbSubsidyAmount > 0) {
      this._appStore.dispatch({ type: 'UPDATE_SBB_PUBLIC_TRANSPORT_DISCOUNT_STATUS', payload: true });
      this._appStore.dispatch({ type: 'SBB_SUBSIDY_STATUS_UPDATE', payload: true });
      console.log('ssb applied');
    }

    if (data.currentBookingReducer.currentBooking.bookingJourney[0].flightData === null) {

      this._appStore.dispatch({
        type: 'UPDATE_FINAL_DESTINATION', payload: {
          airportCode: '',
          airportName: data.flightReducer.flightDetails.flightManualDestination.airportName,
          airportCityName: '',
          airportCountryName: ''
        }
      });

    } else {

      const flightLeg = data.currentBookingReducer.currentBooking.bookingJourney[0].flightData.flightLeg;
      if (flightLeg === 1 || flightLeg === 2) {
        this._appStore.dispatch({
          type: 'UPDATE_FINAL_DESTINATION', payload: {
            airportCode: data.flightReducer.flightDetails.flightResponse.flightStatusDetails[flightLeg - 1].arrivalAirport.airportCode,
            airportName: data.flightReducer.flightDetails.flightResponse.flightStatusDetails[flightLeg - 1].arrivalAirport.airportName,
            airportCityName:
              data.flightReducer.flightDetails.flightResponse.flightStatusDetails[flightLeg - 1].arrivalAirport.airportCityName,
            airportCountryName:
              data.flightReducer.flightDetails.flightResponse.flightStatusDetails[flightLeg - 1].arrivalAirport.airportCountryName
          }
        });
      } else if (flightLeg === 99) {
        this._appStore.dispatch({
          type: 'UPDATE_FINAL_DESTINATION', payload: {
            airportCode: data.flightReducer.flightDetails.flightFinalDestination.airportCode,
            airportName: data.flightReducer.flightDetails.flightFinalDestination.airportName,
            airportCityName: data.flightReducer.flightDetails.flightFinalDestination.airportCityName,
            airportCountryName: ''
          }
        });
      }

    }
  }

  setBookingRefreshState(data) {

    let journeyType = 1;
    let journeyTypeString = 'abc';
    if (data.currentBookingReducer.currentBooking.bookingJourney[0].addOnProductType.length === 0) {
      journeyType = 0;
      journeyTypeString = 'ccd';
    }
    const journeyDirection = data.currentBookingReducer.currentBooking.bookingJourney[0].journeyDirection;
    this.setJourneyDetails(1, journeyType, journeyDirection);

    if (data.bookingReducer.bookingDetails.virtualBagCount === 0 && data.currentBookingReducer.currentBooking.bookingJourney[0].virtualBagCount === 0) {
      this.setVirtualBags(1);
      this.setBagItems({ bags: 1, childEquipment: 0, oversizeItem: 0 });
    } else {
      this.setVirtualBags(data.currentBookingReducer.currentBooking.bookingJourney[0].virtualBagCount);
      // set virtualBagsClassification
      this.setBagItems(this._bagItemsService.BagsClassificationToBagItems(data.currentBookingReducer.currentBooking.bookingJourney[0].virtualBagClassification))
    }

    const flightData = data.flightReducer.flightDetails.flightResponse;

    if (journeyDirection === 2) {
      this._appStore.dispatch({type: 'UPDATE_PARTNER_AIRLINE_CODE', payload: flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].airlineCode});
    } else {
      this._appStore.dispatch({type: 'UPDATE_PARTNER_AIRLINE_CODE', payload: flightData.flightStatusDetails[0].airlineCode});
    }

    // bagsClassification?

    this.setPrice('0');

    if (data.bookingReducer.bookingDetails.bookingType === 'amend') {
      this.amendFlightDestination(data);
    }

    this._appStore.dispatch({ type: 'UPDATE_BOOKING_REFERENCE', payload: data.currentBookingReducer.currentBooking.bookingReference });
    this._appStore.dispatch({ type: 'UPDATE_BOOKING_OBJECT', payload: data.currentBookingReducer.currentBooking });
    this._appStore.dispatch({ type: journeyDirection === 1 ? 'UPDATE_OUT_BOUND_BOOKING_OBJECT' : 'UPDATE_IN_BOUND_BOOKING_OBJECT', payload: data.currentBookingReducer.currentBooking })
    this._appStore.dispatch({ type: 'PANEL_SET', payload: 1 });

    this._dataStorageService.saveData();
    this._dataIncompleteSave = this._dataStorageService.dataSaved$
      .subscribe(res => {
        if (res) {
          this._bookingService.createBookingFromStore(data.currentBookingReducer);
          this._dataIncompleteSave.unsubscribe();
        }
      });
  }

  getBookingStateFromStore(createBooking?: Boolean, isComplete?: Boolean) {
    const res = this.getState(this._appStore);
    this.checkStateItems(res, isComplete, undefined);
  }

  // HTTP CALL TO GET INCOMPLETE - hydrate store with returned data
  // create booking - used on booking form
  getBookingStateFromAPI(createBooking?: Boolean, isComplete?: Boolean) {
    this._dataStorageService.getSavedDataPromise(true).then((res: AppState) => {
      this.checkStateItems(res, isComplete, undefined);
    });
  }

  // check state items
  // checks if booking is complete, user state and booking data
  // refreshes store if required and generates panel path array
  checkStateItems(res, isComplete?, amendFlight?) {
    console.log('store', res, isComplete);
    if (isComplete) {
      this.setStoreRefreshState(res, isComplete, amendFlight);
      // if user has logged in
      // check token is still valid
      if (res.userReducer === null) {
        this.checkUserLogin();
      }

      this.completeBookingRefreshed$.next(true);

      return;
    }

    // is booking paid?
    if (res.bookingReducer.bookingDetails.bookingPaid) {
      // yes - redirect
      this._router.navigate(['flight-finder/search'], { queryParams: { 'start-booking': true } });
    } else {

      // no - refresh data
      // set paths for form if amend
      if (res.bookingReducer.bookingDetails.bookingType === 'amend') {
        // check booking product type
        if (!amendFlight) {
          const isHubdropBooking = this.getState(this._appStore).utilityReducer.isHubdropBooking;
          this._eventsService.generatePathsForAmend(
            this._bookingProduct, this._eventsService.setPathsForAmends(res.bookingReducer.bookingDetails.bookingAmendSection, isHubdropBooking)
          );
        } else {
          this._bookingProduct = res.bookingReducer.bookingDetails.bookingProduct;
          const departureAirportCode = res.flightReducer.flightDetails.flightResponse.flightStatusDetails[0].departureAirport.airportCode;
          const isHubdropBooking = res.utilityReducer.isHubdropBooking;
          const flightStatusDetails = res.flightReducer.flightDetails.flightResponse.flightStatusDetails[0];
          const airlineCode = flightStatusDetails.airlineCode === 'LX' && flightStatusDetails.operatingAirlineCode === 'LX' ? 'LX' : 'None';
          this._eventsService.generatePaths(this._bookingProduct, departureAirportCode, isHubdropBooking, airlineCode);
        }
      } else {
        let isUsaFlight = false;
        if (res.flightReducer.flightDetails.flightResponse?.flightStatusDetails[0]) {
          isUsaFlight = res.flightReducer.flightDetails.flightResponse?.flightStatusDetails[res.flightReducer.flightDetails.flightResponse?.flightStatusDetails.length - 1].arrivalAirport.airportCountryCode.toLocaleUpperCase() === 'US' ? true : false;
        }
        const departureAirportCode = res.flightReducer.flightDetails.flightResponse?.flightStatusDetails[0].departureAirport.airportCode;
        const isHubdropBooking = res.hubDropReducer.selectedPostOffice !== null && res.hubDropReducer.selectedCollectionType === 'atPostOffice';
        const flightStatusDetails = res.flightReducer.flightDetails.flightResponse.flightStatusDetails[0];
        const airlineCode = flightStatusDetails.airlineCode === 'LX' && flightStatusDetails.operatingAirlineCode === 'LX' ? 'LX' : 'None';
        this._eventsService.generatePaths(this._bookingProduct, departureAirportCode, isHubdropBooking, airlineCode, isUsaFlight);
      }

      if (res.bookingReducer.bookingDetails.journeyLegDetails.length === 0) {
        this._router.navigate(['flight-finder/search'], { queryParams: { 'start-booking': true } });
        return;
      }

      // if user has logged in
      // check token is still valid
      if (res.userReducer === null) {
        this.checkUserLogin();
      }

      // contiune refresh after user has been checked
      this.setStoreRefreshState(res, undefined, amendFlight);
      // check if booking object is empty
      // either create new or use data from api
      if (Object.keys(res.currentBookingReducer.currentBooking).length === 0) {
        this._bookingService.createBookingInit(this._bookingProduct);
      } else {
        this._bookingService.createBookingFromStore(res.currentBookingReducer);
      }

    }
  }

  checkUserLogin(userStoreData?, returnError?) {

    let hasError = false;

    const cookieUserToken = this._dataStorageService.checkCookie('DataT');
    if (cookieUserToken === null) {
      this._appStore.dispatch({ type: 'USER_LOADED', payload: true });
      return hasError = true;
    }

    this._loginService.loginUser(false, false, cookieUserToken);

    this._userSavedSubscription = this._loginService.savedUserSubject$
      .subscribe(userValid => {
        if (userValid) {
          this.userLoginValid$.next(true);
        } else {
          this.userLoginValid$.next(false);
        }

        this.userAPIResponse$.next(true);
        this._userSavedSubscription.unsubscribe();

      });
  }

  upgradeProductToABC() {
    this._appStore.dispatch({ type: 'UPDATE_BOOKING_PRODUCT', payload: 'abc' });
    this.setJourneyDetails(1, 1, 1);
    this._bookingService.updateBookingProductToABC();

    this._dataStorageService.saveData();
    this._dataUpgradeSave = this._dataStorageService.dataSaved$
      .subscribe(res => {
        if (res) {
          this.bookingProductUpgrade$.next(true);
          this._dataUpgradeSave.unsubscribe();
        }
      });

  }

  // set journey type and journey direction
  // journeyID = bookingJourney leg index
  // journeyType: 1 = ABC, 2 = CCD, 0 = SDS
  // journeyDirection: GeneralLocationToAirport = 1, AirportToGeneralLocation = 2, AirportToAirport = 3
  setJourneyDetails(id, type, direction) {
    this._appStore.dispatch({ type: 'UPDATE_JOURNEY_LEG', payload: { journeyId: id, journeyType: type, journeyDirection: direction } });
  };
  getProductTypeAsNumber(val) {
    let newVal = 0;
    if (val === 'abc') {
      newVal = 1;
    } else if (val === 'ccd') {
      newVal = 0;
    }
    return newVal;
  }
  getDirectionAsNumber(val) {
    let newVal = 1;
    if (val === 'outbound') {
      newVal = 1;
    } else if (val === 'inbound') {
      newVal = 2;
    }
    return newVal;
  }
  updateJourneyDetails(data) {
    const journeyTypeValue = this.getProductTypeAsNumber(data.productType);
    this.setJourneyDetails(1, journeyTypeValue, data.direction);
  }

  // helper methods to call the store with updates
  resetFlightData() {
    this._appStore.dispatch({ type: 'RESET_FLIGHT_STATE' });
  };
  resetD2dBookingData() {
    this._appStore.dispatch({ type: 'RESET_D2D_STATE' });
  }
  resetFlightFinderData() {
    this._appStore.dispatch({ type: 'RESET_FLIGHT_FINDER' });
    this._appStore.dispatch({ type: 'UPDATE_SESSION_USED', payload: true });
  };
  resetSelectedMultilegFlight() {
    this._appStore.dispatch({ type: 'RESET_SELECTED_MULTILEG_FLIGHT' });
  }
  resetCurrentBookingData() {
    this._appStore.dispatch({ type: 'RESET_BOOKING_OBJECT' });
  };
  resetBookingStateData() {
    this._appStore.dispatch({ type: 'RESET_BOOKING_STATE' });
  };
  resetPriceData() {
    this._appStore.dispatch({ type: 'RESET_PRICE' });
  };
  resetTimeslotData() {
    this._appStore.dispatch({ type: 'RESET_TIMESLOT_STATE' });
  };
  resetUserData() {
    this._appStore.dispatch({ type: 'RESET_USER_STATE' });
  }
  resetPanelNumber() {
    this._appStore.dispatch({ type: 'PANEL_SET', payload: 1 });
  }
  resetPartner() {
    this._appStore.dispatch({ type: 'UPDATE_PARTNER_NAME', payload: { partnerName: 'NONE' } });
  }
  resetUserAgentData() {
    this._appStore.dispatch({ type: 'RESET_USER_AGENT_DATA' });
  }
  resetMilesData() {
    this._appStore.dispatch({ type: 'RESET_MILES' });
  }
  resetUtilityData() {
    this._appStore.dispatch({ type: 'RESET_UTILITY_REDUCER' });
  }
  resetHubDropReducer() {
    this._appStore.dispatch({ type: "RESET_HUBDROP_REDUCER" });
  }
  resetFlybackReducer() {
    this._appStore.dispatch({ type: "RESET_FLYBACK_REDUCER" });
  }
  resetOldBookingInfoReducer() {
    this._appStore.dispatch({ type: "RESET_OLD_BOOKINF_INFO_REDUCER" });
  }
  resetUpsellbookingReducer() {
    this._appStore.dispatch({ type: "RESET_UPSELL_BOOKING" });
  }


  setVirtualBags(virtualBagCount) {
    this._appStore.dispatch({ type: 'UPDATE_VIRTUAL_BAGS', payload: parseInt(virtualBagCount, 10) });
  };

  setBagItems(bagsItemsObj) {
    this._appStore.dispatch({ type: 'UPDATE_BAG_ITEMS', payload: bagsItemsObj });
  };

  setPrice(price) {
    this._appStore.dispatch({ type: 'UPDATE_TOTAL_PRICE', payload: price });
  };
  // setBookingReference(ref) {
  //   this._bookingService.updateBookingReference(ref);
  //   this._appStore.dispatch({ type: 'UPDATE_BOOKING_REFERENCE', payload: ref });
  // }
  setBookingType(type) {
    this._appStore.dispatch({ type: 'UPDATE_BOOKING_TYPE', payload: type });
  }
  setBookingProduct(product) {
    this.bookingProductType$.next(product);
    this._appStore.dispatch({ type: 'UPDATE_BOOKING_PRODUCT', payload: product });
  }
  setFlightFinalDestination(airportCode, airportName, airportCityName) {
    this._appStore.dispatch(
      {
        type: 'UPDATE_FINAL_DESTINATION',
        payload: { airportCode: airportCode, airportName: airportName, airportCityName: airportCityName }
      });
  };
  resetAllFlightData() {
    this._appStore.dispatch({ type: 'RESET_FLIGHT_STATE' });
    this._appStore.dispatch({
      type: 'UPDATE_TIMESLOT', payload: {
        start: '',
        end: '',
        date: '',
        id: '',
        airportTimeslotsWarning: false
      }
    });
    this._bookingService.updateBookingFlight({});
    this._bookingService.updateManualFlightData(initialManualFlightData);
    // this._dataStorageService.saveData();
  }

  // setApiHeader(token) {
  //   const headers = new Headers();
  //   console.log(this.createAuthorizationHeader(headers, token));
  //   return this.createAuthorizationHeader(headers, token);
  // }
  // getApiHeader() {
  //   return this.header;
  // }

  // createAuthorizationHeader(headers: Headers, token) {
  //   headers.append('Authorization', 'bearer ' + token);
  // }



};
